import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe2, MessageCircle, HeartPulse, Building, Users2, GraduationCap, CheckCircle2, Wifi, Clock, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHero, Section, SectionHeading } from "@/components/blocks";
import WorkshopInterestDialog from "@/components/WorkshopInterestDialog";
import BilingualGuides from "@/components/BilingualGuides";
import api, { API } from "@/lib/api";

const PILLARS = [
  { icon: Building, title: "Australian School Culture", items: ["Staffroom expectations", "Professional language", "Parent communication", "School systems"] },
  { icon: MessageCircle, title: "Communication Confidence", items: ["Speaking in meetings", "Asking for feedback", "Classroom language"] },
  { icon: HeartPulse, title: "Thriving on Placement", items: ["Managing anxiety", "Cultural adaptation", "Mentor relationships"] },
];

export default function InternationalTeachers() {
  const [workshops, setWorkshops] = useState([]);
  useEffect(() => {
    api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="international-teachers-page">
      <PageHero
        eyebrow="Flagship Program · International Teachers"
        title="Transition into Australian schools with confidence."
        subtitle="Understanding school culture, communication and confidence — the flagship program that helps international educators thrive, not just survive."
        image="https://images.pexels.com/photos/8419197/pexels-photo-8419197.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <SectionHeading eyebrow="Three pillars of success" title="From uncertainty to belonging" center />
        <div className="grid md:grid-cols-3 gap-6">
          {PILLARS.map((p) => (
            <Card key={p.title} className="p-8 border-slate-200" data-testid={`int-pillar-${p.title}`}>
              <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center mb-5"><p.icon className="w-6 h-6 text-white" /></div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-4">{p.title}</h3>
              <ul className="space-y-3">
                {p.items.map((it) => <li key={it} className="flex items-start gap-2 text-slate-600"><CheckCircle2 className="w-4 h-4 text-teal-500 mt-1 shrink-0" /> {it}</li>)}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      <div className="bg-slate-50 border-y border-slate-200" id="bilingual-guides">
        <Section>
          <SectionHeading eyebrow="Bilingual Guides · 双语指南" title="Free English–Mandarin guides for the Australian classroom" subtitle="Slang, idioms, acronyms and everyday teaching phrases — every entry translated into Mandarin. Free for any pre-service teacher; sign in with Google to download." />
          <BilingualGuides />
        </Section>
      </div>

      <div className="bg-slate-900" id="workshops">
        <Section>
          <SectionHeading eyebrow="The Workshop Program" title="Ten workshops. One confident educator." subtitle="Register your interest and we'll notify you first as dates are confirmed." light />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {workshops.map((w, i) => (
              <div key={w.id} className="rounded-xl bg-slate-800/60 border border-slate-700 overflow-hidden hover:border-emerald-500 transition-colors" data-testid={`int-workshop-${w.id}`}>
                {w.has_sample && (
                  <a href={`${API}/resources/${w.deck_id}/preview`} target="_blank" rel="noreferrer"
                     className="block border-b border-slate-700 bg-slate-900" data-testid={`int-workshop-sample-${w.id}`}>
                    <img src={`${API}/resources/${w.deck_id}/thumbnail`} loading="lazy"
                         alt={`${w.title} — sample slide`} className="w-full aspect-[16/9] object-cover object-top opacity-90 hover:opacity-100 transition-opacity" />
                  </a>
                )}
                <div className="flex items-center gap-4 justify-between p-5">
                <div className="flex items-start gap-4">
                  <span className="text-2xl font-bold font-display text-emerald-400 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h4 className="font-display font-semibold text-white leading-tight">{w.title}</h4>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Dates to be announced</span>
                      <span className="flex items-center gap-1"><Wifi className="w-3 h-3" /> {w.mode}</span>
                    </div>
                  </div>
                </div>
                <WorkshopInterestDialog workshop={w} triggerClassName="bg-emerald-600 hover:bg-emerald-700 rounded-full shrink-0" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-xl border border-slate-700 bg-slate-800/60 p-6" id="bundles">
            <h3 className="font-display text-lg font-semibold text-white">Can't wait for a live session?</h3>
            <p className="text-sm text-slate-300 mt-1.5">
              Every workshop slide deck is available now as an instant download — $19 each, or bundle and save.
            </p>
            <Button asChild className="mt-4 bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid="intl-decks-link">
              <Link to="/resources">Browse workshop decks <ArrowRight className="w-4 h-4" /></Link>
            </Button>
          </div>
        </Section>
      </div>
    </div>
  );
}
